import _ from "lodash";
import React from "react";
import classes from "./HelperPane.scss";
import { CloseOutlined, HelpBulbOutlined } from "@toddle-edu/ds-icons";
import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import withLoader from "UIComponents/GqlWithLoader";
import I18nHOC from "UIComponents/I18nHOC";
import MediaModal from "UIComponents/MediaModal";
import {
  getHelpTextQuery,
  getTemplateFieldPropertiesQuery,
} from "modules/CommonQuery";
import {
  getHelpTextFromCache,
  getTemplateFieldPropertiesFromCache,
} from "modules/CommonGraphqlHelpers";
import ReactHtmlParser, { convertNodeToElement } from "react-html-parser";
import {
  generateRandomId,
  getUpdatedResourceLinkForRegion,
  onEnterOrSpaceKeyDown,
} from "Utils";
import { getPropertyValueFromConfigLevels } from "UnitPlans/modules/Utils";
import { Button, IconButton } from "@toddle-edu/ds-web";
import Header from "./Header";
import classNames from "classnames";

const RIGHT_PANE_ANIMATION_TIME = 300;

class HelperPane extends React.PureComponent {
  constructor(props) {
    super(props);
    this.containerRef = null;
    this.previousActiveElement = null;
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;

    this.state = {
      showMediaModal: false,
      mediaModalData: {},
    };
  }

  toggleMediaModal = val => {
    this.setState({
      showMediaModal: val,
    });
    // focusing back the container again when media modal is closed
    if (!val && this.containerRef) {
      this.containerRef.focus();
    }
  };

  componentDidMount() {
    if (this.containerRef) {
      // trapping focus logic
      const focusableElements = _.toArray(
        this.containerRef.querySelectorAll('a, button[id], [tabindex="0"]')
      );
      this.firstFocusableElement = focusableElements[0];
      this.lastFocusableElement =
        focusableElements[focusableElements.length - 1];

      // auto focusing on the container when it is mounted
      // 300 ms delay is added to make sure that it is focused after the animation is completed
      if (document.activeElement !== this.containerRef) {
        setTimeout(() => this.containerRef?.focus(), RIGHT_PANE_ANIMATION_TIME);
      }
    }

    this.containerRef.addEventListener("keydown", this.handleKeyDown);
  }

  componentWillUnmount() {
    if (this.containerRef) {
      this.containerRef.removeEventListener("keydown", this.handleKeyDown);
    }
  }

  stopPropagationAndPreventDefault = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  handleKeyDown = event => {
    const { key } = event;

    if (key === "Tab") {
      const currentFocusedElement = document.activeElement;
      if (event.shiftKey) {
        // Shift + Tab
        // if the current focused element is the container or the first focusable element
        // and shift + tab is pressed, then focus the last focusable element
        if (
          currentFocusedElement === this.containerRef ||
          currentFocusedElement === this.firstFocusableElement
        ) {
          this.stopPropagationAndPreventDefault(event);
          this.lastFocusableElement.focus();
        }
      } else {
        // Tab
        // if there is only one focusable element or the last focusable element in the DOM was previously focused
        if (
          this.firstFocusableElement === this.lastFocusableElement ||
          this.lastFocusableElement === this.previousActiveElement
        ) {
          this.stopPropagationAndPreventDefault(event);
          this.firstFocusableElement?.focus();
        }
      }
      this.previousActiveElement = currentFocusedElement;
    } else if (key === "Escape") {
      this.props.onClose();
    }
  };

  onClickImage = e => {
    this.toggleMediaModal(true);
    this.setState({
      mediaModalData: {
        attachments: [
          {
            type: "IMAGE",
            id: generateRandomId(),
            url: e.currentTarget.getAttribute("src"),
            name: e.currentTarget.getAttribute("alt") ?? "Image",
          },
        ],
      },
    });
  };

  handleKeyDownOnImage = e => {
    onEnterOrSpaceKeyDown(e, this.onClickImage);
  };

  //it will be executed for every node i.e. div, span, img etc.
  /*
node: will have following details.
  type (string): The type of node (tag, text, style etc)
  name (string): The name of the node
  children (array): Array of children nodes
  next (node): The node's next sibling
  prev (node): The node's previous sibling
  parent (node): The node's parent
  data (string): The text content, if the type is text

ref:https://www.npmjs.com/package/react-html-parser
 */
  htmlNodeTransform = node => {
    const { t, testId } = this.props;
    if (node.type === "tag" && node.name === "img") {
      const updatedSrc = getUpdatedResourceLinkForRegion({
        link: node.attribs.src,
      });
      const { style } = node.attribs;
      const camelize = string =>
        string.replace(/-([a-z])/gi, (s, group) => group.toUpperCase());
      const style2object = style =>
        style
          ?.split(";")
          ?.filter(s => s.length)
          ?.reduce((a, b) => {
            const keyValue = b.split(":");
            a[camelize(keyValue[0])] = keyValue[1];
            return a;
          }, {});

      return (
        <div
          className={classes.imageContainer}
          src={updatedSrc}
          alt={node.attribs.alt}
          onClick={this.onClickImage}
          tabIndex={0}
          onKeyDown={this.handleKeyDownOnImage}
          data-test-id={`${testId}-helperPane-imageContainer`}
        >
          <img
            {..._.omit(node.attribs, ["style"])}
            style={style2object(style)}
            src={updatedSrc}
            alt={node.attribs.alt || ""}
          ></img>
          <div className={classes.hoverContainer}>
            <Button testId={`${testId}-helperPane-image`}>
              {t("common:view_with_label_lowercase", {
                label: t("common:image"),
              })}
            </Button>
          </div>
        </div>
      );
    }
    if (node.type === "tag" && node.name === "a") {
      const updatedHref = getUpdatedResourceLinkForRegion({
        link: node.attribs.href,
      });
      return (
        <a
          {...node.attribs}
          href={updatedHref}
          target="_blank"
          rel="noreferrer"
        >
          {node.children.map((childNode, childIdx) =>
            convertNodeToElement(childNode, childIdx, this.htmlNodeTransform)
          )}
        </a>
      );
    }
  };

  renderHeader() {
    const {
      header,
      onClose,
      testId,
      dsVersion,
      helperText: { title = "" } = {},
      label,
    } = this.props;

    const isDSV2 = dsVersion === "2.0";
    const headerTitle = title || label;

    if (header) {
      return header;
    }

    if (isDSV2) {
      return <Header testId={testId} onClose={onClose} title={headerTitle} />;
    }

    return (
      <div className={classes.header}>
        <div className={classes.helperBulb}>
          <HelpBulbOutlined variant="link" size="small" />
        </div>
        <IconButton
          icon={<CloseOutlined iconWeight="bold" />}
          variant="plain"
          onClick={onClose}
          id={"HELPER_PANE_CLOSE_ICON"}
          testId={`${testId}-helperPane-close`}
        />
      </div>
    );
  }

  render() {
    let { helperText: { title = "" } = {} } = this.props;

    const {
      onClose,
      helperText: { subtitle = "", content = "" } = {},
      label,
      testId = "",
      dsVersion,
      isSinglePageAndNewUnitView,
    } = this.props;

    const isDSV2 = dsVersion === "2.0";

    if (!title) title = label;

    const helperBodyClasses = classNames({
      [classes.helperBody]: !isDSV2,
      [classes.helperBodyDSV2]: isDSV2,
      [classes.helperBodySinglePage]: isSinglePageAndNewUnitView,
    });

    const helperContentClasses = classNames({
      [classes.helperContent]: !isDSV2,
      [classes.helperContentV2]: isDSV2,
      "text-body": isDSV2,
    });

    const { showMediaModal, mediaModalData } = this.state;
    return (
      <div
        className={classes.container}
        ref={ref => (this.containerRef = ref)}
        tabIndex={-1}
      >
        <div
          className={classNames(classes.scrollContainer, {
            [classes.scrollContainerV2]: isSinglePageAndNewUnitView,
          })}
        >
          {this.renderHeader()}
          <div className={helperBodyClasses}>
            {isDSV2 ? null : (
              <>
                <div className={classes.helperTitle}>{title}</div>
                <div className={classes.helperSubTitle}>{subtitle}</div>
              </>
            )}
            <div className={helperContentClasses}>
              {ReactHtmlParser(content, {
                transform: this.htmlNodeTransform,
              })}
            </div>
          </div>
        </div>
        {showMediaModal && (
          <MediaModal
            attachments={mediaModalData.attachments}
            toggleMediaModal={this.toggleMediaModal}
          />
        )}
      </div>
    );
  }
}

const mapActionCreators = {};

const mapStateToProps = (state, ownProps) => {
  const { i18n, isPortedField, helperTextId, curriculumProgramId } = ownProps;

  let templateFieldQueryVariables = {};

  if (isPortedField) {
    templateFieldQueryVariables = {
      templateFieldId: helperTextId,
      configFilter: { properties: ["helpTextContent"] },
      valueFilter: { locale: i18n.language, curriculumProgramId },
    };
  }

  return {
    locale: i18n.language,
    showLoaderBar: false,
    isData: true,
    isLoading: false,
    templateFieldQueryVariables,
  };
};

export default compose(
  I18nHOC({ resource: ["unitPlanTemplate", "projectTemplate"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getHelpTextQuery, {
    name: "getHelpText",
    skip: ({ isPortedField, helperTextId }) => isPortedField || !helperTextId,
    options: ({ helperTextId, locale }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: helperTextId,
        locale,
      },
    }),
    props({
      getHelpText,
      ownProps: { helperTextId, locale, isData, isLoading },
    }) {
      const heplerText = getHelpTextFromCache({ id: helperTextId, locale });
      return {
        isData: !_.isEmpty(heplerText) && isData,
        helperText: heplerText,
        networkStatus: getHelpText.networkStatus,
        isLoading:
          getHelpText["networkStatus"] == 1 ||
          getHelpText["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  graphql(getTemplateFieldPropertiesQuery, {
    name: "getTemplateFieldProperties",
    alias: "getTemplateFieldProperties",
    skip: ({ isPortedField }) => !isPortedField,
    options: ({ templateFieldQueryVariables }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: templateFieldQueryVariables,
      };
    },
    props: ({
      getTemplateFieldProperties: { networkStatus },
      ownProps: { templateFieldQueryVariables, isData, isLoading, templateId },
    }) => {
      const templateFieldData = getTemplateFieldPropertiesFromCache(
        templateFieldQueryVariables
      );

      const isTemplateFieldDataPresent = !_.isEmpty(templateFieldData);

      let helperText = {};

      if (isTemplateFieldDataPresent) {
        const fieldProperties = _.get(templateFieldData, "config", []);
        const values = _.get(
          _.find(
            fieldProperties,
            ({ globalFieldProperty }) =>
              _.get(globalFieldProperty, "property") === "helpTextContent"
          ),
          "values",
          []
        );
        const content = getPropertyValueFromConfigLevels({
          values,
          templateId,
        });
        helperText = { content };
      }

      return {
        isData: isTemplateFieldDataPresent && isData,
        isLoading:
          _.includes(GRAPHQL_ONGOING_QUERY_NETWORK_STATUS, networkStatus) ||
          isLoading,
        helperText,
      };
    },
  }),
  withLoader
)(HelperPane);
